import { safeParseDate } from "./date";
import { formatYmdLocal } from "./formatYmdLocal";

export function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * Parses appointment API datetimes. Date-only strings (YYYY-MM-DD) are treated as
 * local calendar dates, not UTC midnight.
 */
export function parseAppointmentApiDateTime(
  value: Date | string | null | undefined,
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Calendar date for grouping/display.
 *
 * Schedule slots from the API use UTC date components as the studio calendar day
 * (e.g. 2026-06-16T10:00:00Z = June 16 at 10:00 studio wall clock).
 * Booking lists use the same rule so the booked day matches the schedule picker.
 */
export function getAppointmentCalendarDate(
  value: Date | string | null | undefined,
): Date | null {
  const parsed = parseAppointmentApiDateTime(value) ?? safeParseDate(value);
  if (!parsed) return null;

  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    12,
    0,
    0,
  );
}

/**
 * Date key for a slot from GET /api/appointments/{id}/schedule.
 * Matches how the backend buckets slots by calendar day.
 */
export function getAppointmentScheduleSlotDateKey(
  value: Date | string | null | undefined,
): string | null {
  const parsed = parseAppointmentApiDateTime(value) ?? safeParseDate(value);
  if (!parsed) return null;

  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const d = String(parsed.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Date key for a calendar grid cell (the labeled day the user clicks).
 * Uses civil year/month/day - never toISOString().
 */
export function getAppointmentCalendarCellDateKey(date: Date): string {
  return formatYmdLocal(date);
}

/** @deprecated Prefer getAppointmentScheduleSlotDateKey for schedule slots. */
export function getAppointmentLocalDateKey(
  value: Date | string | null | undefined,
): string | null {
  return getAppointmentScheduleSlotDateKey(value);
}

export function isSameAppointmentScheduleDay(
  cellDate: Date,
  slotValue: Date | string | null | undefined,
): boolean {
  const cellKey = getAppointmentCalendarCellDateKey(cellDate);
  const slotKey = getAppointmentScheduleSlotDateKey(slotValue);
  return slotKey != null && cellKey === slotKey;
}

export function isSameAppointmentLocalDay(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): boolean {
  const keyA = getAppointmentScheduleSlotDateKey(a);
  const keyB = getAppointmentScheduleSlotDateKey(b);
  return keyA != null && keyB != null && keyA === keyB;
}
